import type { IPageDescriptor } from './theopolis';

/**
 * Pages the header-bar chat agent is allowed to navigate
 * the operator to. Shipped on every chat send from `ChatLauncher`; theopolis
 * forwards it into the navigation tools (`list_website_pages` /
 * `navigate_to_page`) so the LLM can pick a target. The console intercepts
 * the resulting `tool-call` SSE event and calls its router.
 *
 * Theopolis stays agnostic — it never imports anything from this file.
 *
 * Curation guidelines:
 *  - Add the routes a user would plausibly say "take me to ___" about.
 *  - Skip detail pages that need an id the LLM can't possibly know
 *    (e.g. "/workflows/:id" makes sense only if the user already named
 *    a workflow in conversation; that's fine — keep it).
 *  - Keep `description` short and operator-facing; the LLM uses it to
 *    decide which page matches the user's intent.
 *  - URL templates use TanStack Router's `:param` syntax so the LLM can
 *    fill in concrete IDs from conversation context.
 */
export const pageCatalog: IPageDescriptor[] = [
  // ── Top-level ─────────────────────────────────────────────────────────
  {
    name: 'Home',
    description: 'Console home page with summary cards.',
    urlTemplate: '/',
  },
  {
    name: 'Settings',
    description: 'Org-wide settings, including refinement preferences.',
    urlTemplate: '/settings',
  },
  {
    name: 'Skills',
    description: 'Org-managed agent skills mounted into chat agents.',
    urlTemplate: '/agents/skills',
  },

  // ── Workflows ─────────────────────────────────────────────────────────
  {
    name: 'Workflows',
    description: 'List of all workflows in the org.',
    urlTemplate: '/workflows',
  },
  {
    name: 'New workflow',
    description: 'Create a new workflow.',
    urlTemplate: '/workflows/new',
  },
  {
    name: 'Workflow detail',
    description: "A single workflow's runs, versions, and structure.",
    urlTemplate: '/workflows/:id',
  },
  {
    name: 'Workflow editor',
    description: "Visual editor for a workflow's nodes and edges.",
    urlTemplate: '/workflows/:id/editor',
  },

  // ── Incidents (unified: Inbox / Board / Coordinator views) ───────────
  {
    name: 'Incident board',
    description: 'Kanban board of workflow runs, grouped by outcome or status.',
    urlTemplate: '/inbox/board',
  },
  {
    name: 'Coordinator',
    description: 'Live view of the coordinator deduplicating and routing signals.',
    urlTemplate: '/inbox/coordinator',
  },
  {
    name: 'Run detail',
    description: 'Trace and result of a single workflow run.',
    urlTemplate: '/triage/runs/:id',
  },
  {
    name: 'HITL detail',
    description: 'Review a single run that needs human input before continuing.',
    urlTemplate: '/inbox/hitl/:runId',
  },
  {
    name: 'Board columns',
    description: 'Configure the outcome columns of the incident board.',
    urlTemplate: '/board-columns',
  },

  // ── Evaluations ───────────────────────────────────────────────────────
  {
    name: 'Evaluations',
    description: 'Evaluation runs that score Workflow versions against datasets.',
    urlTemplate: '/evals',
  },
  {
    name: 'Evaluation detail',
    description: 'A single evaluation run, its scores and per-test-case results.',
    urlTemplate: '/evals/:id',
  },
  {
    name: 'Ground truths',
    description: 'Curated correct answers evaluations score workflows against.',
    urlTemplate: '/ground-truth',
  },
  {
    name: 'Judges',
    description: 'LLM judges used as graders in evaluations.',
    urlTemplate: '/judges',
  },
  {
    name: 'Suites',
    description: 'Eval tasks and the suites that group them — one management surface.',
    urlTemplate: '/suites',
  },
  {
    name: 'Feedback',
    description: 'Operator feedback on workflow output, promotable into ground truths.',
    urlTemplate: '/feedback',
  },

  // ── Document sets ─────────────────────────────────────────────────────
  {
    name: 'Document sets',
    description:
      'Versioned collections of expert-written documents about the environments Workflows watch.',
    urlTemplate: '/document-sets',
  },
  {
    name: 'Document set detail',
    description: "A single document set's files and committed versions.",
    urlTemplate: '/document-sets/:id',
  },

  // ── Agents / Models / MCP ─────────────────────────────────────────────
  {
    name: 'Agents',
    description: 'List of agents in the org (with Skills and Memories views).',
    urlTemplate: '/agents',
  },
  {
    name: 'New agent',
    description: 'Create a new agent.',
    urlTemplate: '/agents/new',
  },
  {
    name: 'Agent detail',
    description: "A single agent's configuration, prompts, and tools.",
    urlTemplate: '/agents/:id',
  },
  {
    name: 'Orchestration signals',
    description: 'Thread-scoped wake signals for live sessions (pick a thread, attach pollers).',
    urlTemplate: '/agents/orchestration/signals',
  },
  {
    name: 'Models',
    description: 'LLM model configurations.',
    urlTemplate: '/models',
  },
  {
    name: 'New model',
    description: 'Create a new model configuration.',
    urlTemplate: '/models/new',
  },
  {
    name: 'Model detail',
    description: "A single model configuration's provider, name, and settings.",
    urlTemplate: '/models/:id',
  },
  {
    name: 'MCP connections',
    description: 'Outbound MCP server connections that expose tools to agents.',
    urlTemplate: '/mcp-connections',
  },
  {
    name: 'App connections',
    description: 'Third-party app integrations connected to the org.',
    urlTemplate: '/app-connections',
  },
  {
    name: 'API keys',
    description: 'Personal access tokens for connecting to the MCP server headlessly.',
    urlTemplate: '/api-keys',
  },

  // ── Memory ────────────────────────────────────────────────────────────
  {
    name: 'Memories',
    description: 'Personal, org-wide, and per-agent memories agents carry across sessions.',
    urlTemplate: '/agents/memories',
  },

  // ── Internal (superuser cross-org operations) ────────────────────────
  {
    name: 'Internal',
    description: 'Internal home — superuser cross-org operations.',
    urlTemplate: '/internal',
  },
  {
    name: 'Organizations',
    description: 'List of all organizations (superuser only).',
    urlTemplate: '/internal/organizations',
  },
  {
    name: 'New organization',
    description: 'Create a new organization (superuser only).',
    urlTemplate: '/internal/organizations/new',
  },
  {
    name: 'Organization detail',
    description: "A single organization's settings (superuser only).",
    urlTemplate: '/internal/organizations/:id',
  },
  // ── Knowledge (still superuser-gated until org-level flags land) ──────
  {
    name: 'Internal pipelines',
    description: 'Cross-org pipeline configs (superuser only).',
    urlTemplate: '/internal/pipelines',
  },
  {
    name: 'New pipeline',
    description: 'Create a new pipeline config (superuser only).',
    urlTemplate: '/internal/pipelines/new',
  },
  {
    name: 'Pipeline detail',
    description: "A single pipeline config's blueprints, sources, and runs (superuser only).",
    urlTemplate: '/internal/pipelines/:id',
  },
  {
    name: 'Blueprints',
    description: 'Reusable Blueprint recipes that drive Pipelines (superuser only).',
    urlTemplate: '/internal/blueprints',
  },
  {
    name: 'New blueprint',
    description: 'Create a new Blueprint (superuser only).',
    urlTemplate: '/internal/blueprints/new',
  },
  {
    name: 'Blueprint detail',
    description: "A single Blueprint's prompts and target Artifacts (superuser only).",
    urlTemplate: '/internal/blueprints/:id',
  },
  {
    name: 'Knowledge graphs',
    description:
      'Visualize, query (RAG), and inspect the knowledge graph for any pipeline (superuser only).',
    urlTemplate: '/internal/knowledge-graphs',
  },
];
